import AppDispatcher from './AppDispatcher';
import { ActionTypes } from './Constants';
import {
   EmailAuthProvider,
   onAuthStateChanged,
   reauthenticateWithCredential,
   signInWithEmailAndPassword,
   signOut,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import {
   type Project,
   type Scene,
   type Character,
   type Location,
   type Plot,
} from '../types/entities';
import {
   initPouch,
   startRemoteSync,
   stopSync,
   destroyLocalDatabase,
   isSyncRunning,
   fetchByType,
   createDoc,
   updateDocById,
   deleteDocById,
   putDocAttachment,
   getAttachmentBlob,
   getAttachmentUrl,
   revokeAttachmentUrl,
   clearAttachmentUrlCache,
   resetSyncBlock,
   getSyncBlockedReason,
   DocumentType,
} from '../database';
import FirebaseAuthErrorMapper from '../lib/firebase-auth-error-mapper';
import {
   persistCouchCreds,
   readCouchCreds,
   clearCouchCreds,
   areCredentialsFresh,
   migrateLegacyCredentials,
   type CouchCreds,
   type CouchCredsWithMetadata,
} from '../lib/secure-storage';
import {
   QUILL_PROJECT_FORMAT,
   QUILL_PROJECT_VERSION,
   type QuillProjectAsset,
   type QuillProjectFile,
} from '../types/project-file';

type CouchStatus = 'unknown' | 'ready' | 'pending' | 'error';

/**
 * Structured result from CouchDB preparation.
 * Replaces silent success/failure with explicit outcomes.
 */
export type PrepareResult =
   | { ok: true; couch: { url: string; username: string; password: string } }
   | { ok: false; code: string; retryable: boolean; message?: string };

/**
 * Error codes for PrepareResult
 */
const PrepareErrorCodes = {
   NETWORK_ERROR: 'NETWORK_ERROR',
   AUTH_FAILED: 'AUTH_FAILED',
   SERVER_ERROR: 'SERVER_ERROR',
   NO_USER: 'NO_USER',
   INVALID_RESPONSE: 'INVALID_RESPONSE',
   TIMEOUT: 'TIMEOUT',
} as const;

const imageAttachmentId = 'image';
let currentAuthInitialization: Promise<void> | null = null; // Race condition guard

const stripProjectFileInternals = <T extends Record<string, unknown>>(entity: T): T => {
   const {
      _id,
      _rev,
      _attachments,
      _deleted,
      type,
      userId,
      imageUrl,
      ...portable
   } = entity;

   if (typeof imageUrl === 'string' && !imageUrl.startsWith('blob:')) {
      return { ...portable, imageUrl } as unknown as T;
   }
   return portable as unknown as T;
};

const blobToBase64 = async (blob: Blob): Promise<string> => {
   const bytes = new Uint8Array(await blob.arrayBuffer());
   const chunkSize = 0x8000;
   let binary = '';
   for (let offset = 0; offset < bytes.length; offset += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
   }
   return btoa(binary);
};

const base64ToBlob = (data: string, contentType: string): Blob => {
   let binary: string;
   try {
      binary = atob(data);
   } catch {
      throw new Error('An embedded project asset is corrupt.');
   }

   const bytes = new Uint8Array(binary.length);
   for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
   }
   return new Blob([bytes], { type: contentType });
};

const cleanImportedEntity = <T>(entity: T): Partial<T> => {
   const {
      _id,
      _rev,
      _attachments,
      _deleted,
      id,
      type,
      userId,
      ...clean
   } = entity as T & Record<string, unknown>;
   return clean as Partial<T>;
};

const remapIds = (
   ids: unknown,
   idMap: Map<string, string>,
): string[] | undefined => {
   if (!Array.isArray(ids)) return undefined;
   return ids
      .map((id) => (typeof id === 'string' ? idMap.get(id) : undefined))
      .filter((id): id is string => Boolean(id));
};

const dispatchCouchStatus = (status: CouchStatus, message?: string | null) => {
   AppDispatcher.dispatch({
      type: ActionTypes.COUCH_STATUS_CHANGED,
      couchStatus: status,
      couchMessage: message || null,
   });
};

const shouldPersistImageUrl = (imageUrl?: string | null) =>
   Boolean(imageUrl && !imageUrl.startsWith('blob:'));
const isRemoteImageUrl = (imageUrl?: string | null) =>
   Boolean(imageUrl && /^https?:\/\//i.test(imageUrl));

const hydrateImageUrl = async <
   T extends { id?: string; imageUrl?: string | null; imageAttachmentId?: string | null },
>(
   entity: T,
): Promise<T> => {
   if (!entity.id) return entity;
   if (entity.imageAttachmentId) {
      try {
         const url = await getAttachmentUrl(entity.id, entity.imageAttachmentId);
         if (url) {
            return { ...entity, imageUrl: url };
         }
      } catch (err) {
         console.warn('Unable to load image attachment', err);
      }
      return entity;
   }

   if (isRemoteImageUrl(entity.imageUrl)) {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
         return entity;
      }
      try {
         const response = await fetch(entity.imageUrl as string);
         if (!response.ok) {
            return entity;
         }
         const blob = await response.blob();
         revokeAttachmentUrl(entity.id, imageAttachmentId);
         await putDocAttachment(entity.id, imageAttachmentId, blob, blob.type);
         const updated = await updateDocById<T>(entity.id, {
            imageAttachmentId,
            imageUrl: null,
         } as Partial<T>);
         const url = await getAttachmentUrl(entity.id, imageAttachmentId);
         return {
            ...(updated as T),
            ...(url ? { imageUrl: url } : {}),
         };
      } catch (err) {
         console.warn('Unable to cache remote image attachment', err);
      }
   }

   return entity;
};

const attachImageToDoc = async <
   T extends { id?: string; imageUrl?: string | null; imageAttachmentId?: string | null },
>(
   docId: string,
   file: File,
): Promise<T & { imageUrl?: string | null }> => {
   revokeAttachmentUrl(docId, imageAttachmentId);
   await putDocAttachment(docId, imageAttachmentId, file, file.type);
   const updated = await updateDocById<T>(docId, {
      imageAttachmentId,
      imageUrl: null,
   } as any);
   const url = await getAttachmentUrl(docId, imageAttachmentId);
   return {
      ...updated,
      ...(url ? { imageUrl: url } : {}),
   };
};

const requireUserId = () => {
   const uid = auth.currentUser?.uid;
   if (!uid) throw new Error('User not authenticated');
   return uid;
};

const getErrorMessage = (error: unknown): string => {
   // First try to map Firebase auth errors to user-friendly messages
   if (error instanceof Error && 'code' in error) {
      const firebaseError = error as { code: string; message: string };
      if (FirebaseAuthErrorMapper.isFirebaseAuthError(firebaseError.code)) {
         return FirebaseAuthErrorMapper.mapError(firebaseError);
      }
   }

   // Fallback to original logic for non-Firebase errors
   if (error instanceof Error) return error.message;
   if (typeof error === 'object' && error && 'message' in (error as Record<string, unknown>)) {
      return String((error as Record<string, unknown>).message);
   }
   return 'Unknown error';
};

type EnsureCouchResponse = {
   status?: CouchStatus | 'offline';
   message?: string;
   dbUrl?: string;
   auth?: { username?: string; password?: string };
   couch?: {
      dbUrl?: string;
      username?: string;
      password?: string;
   };
};

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5001';

const extractCouchCredentials = (
   data: EnsureCouchResponse,
): { url: string; username: string; password: string } | null => {
   if (data.dbUrl && data.auth?.username && data.auth?.password) {
      return {
         url: data.dbUrl,
         username: data.auth.username,
         password: data.auth.password,
      };
   }

   if (data.couch?.dbUrl && data.couch?.username && data.couch?.password) {
      return {
         url: data.couch.dbUrl,
         username: data.couch.username,
         password: data.couch.password,
      };
   }

   return null;
};

/**
 * Prepares CouchDB for the current user.
 * This function is idempotent - calling it multiple times will not:
 * - duplicate users
 * - throw if DB exists
 * - corrupt _security
 * - reset replication improperly
 *
 * @returns PrepareResult - structured result indicating success or failure
 */
const ensureCouchForUser = async (options?: { forceReset?: boolean }): Promise<PrepareResult> => {
   const user = auth.currentUser;
   if (!user) {
      return { ok: false, code: PrepareErrorCodes.NO_USER, retryable: false, message: 'User not authenticated' };
   }

   // Dispatch PROVISIONING state
   AppDispatcher.dispatch({ type: ActionTypes.WORKSPACE_PROVISIONING });

   try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/couch/ensure`, {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         body: JSON.stringify({ firebaseToken: token, forceReset: options?.forceReset }),
      });
      const data = (await res.json().catch(() => ({}))) as EnsureCouchResponse;

      // Check for server errors
      if (!res.ok) {
         const isAuthError = res.status === 401 || res.status === 403;
         const code = isAuthError ? PrepareErrorCodes.AUTH_FAILED : PrepareErrorCodes.SERVER_ERROR;
         const retryable = !isAuthError && res.status >= 500;

         dispatchCouchStatus('error', data.message || `Server error: ${res.status}`);

         return { ok: false, code, retryable, message: data.message };
      }

      const couch = extractCouchCredentials(data);

      if (data.status === 'offline') {
         dispatchCouchStatus('error', data.message || 'CouchDB is unreachable.');
         return {
            ok: false,
            code: PrepareErrorCodes.NETWORK_ERROR,
            retryable: false,
            message: data.message || 'CouchDB is unreachable.',
         };
      }

      // Validate response has required credentials
      if (!couch) {
         dispatchCouchStatus('pending', 'Database provisioning in progress...');
         return {
            ok: false,
            code: PrepareErrorCodes.INVALID_RESPONSE,
            retryable: false,
            message: 'Credentials not yet available',
         };
      }

      // Persist credentials before initializing (for recovery)
      await persistCouchCreds(user.uid, couch);

      // Success - dispatch READY state
      dispatchCouchStatus('ready', null);
      AppDispatcher.dispatch({ type: ActionTypes.WORKSPACE_READY });

      return { ok: true, couch };
   } catch (err) {
      console.error('Ensure Couch failed:', err);
      const errorMessage = String((err as Error)?.message || 'Database setup failed.');
      const isNetworkIssue = errorMessage.toLowerCase().includes('network') ||
                            errorMessage.toLowerCase().includes('fetch') ||
                            errorMessage.toLowerCase().includes('timeout');

      const code = isNetworkIssue ? PrepareErrorCodes.NETWORK_ERROR : PrepareErrorCodes.SERVER_ERROR;

      dispatchCouchStatus('error', errorMessage);

      return { ok: false, code, retryable: isNetworkIssue, message: errorMessage };
   }
};

const Actions = {
   // Auth Actions
   login: async (email: string, password: string) => {
      try {
         console.log('[Login] Starting login flow');
         const userCredential = await signInWithEmailAndPassword(auth, email, password);

         // Step 1: Dispatch login success immediately (user is AUTHENTICATED)
         // Local writing is now allowed
         AppDispatcher.dispatch({
            type: ActionTypes.LOGIN_SUCCESS,
            user: userCredential.user,
            couchStatus: 'pending',
            couchMessage: 'Preparing workspace...',
         });

         // Step 2: Prepare Couch
         console.log('[Login] Fetching DB credentials from backend');
         const token = await userCredential.user.getIdToken();
         let creds = await fetchDbCredentials('/login', { firebaseToken: token, email });

         // New users: /login may return empty creds if CouchDB is still provisioning.
         // initAuthListener runs concurrently and may provision via /couch/ensure instead.
         if (!creds || !creds.dbUrl || !creds.auth?.username || !creds.auth?.password) {
            dispatchCouchStatus('pending', 'Setting up your database, please wait...');
            for (let attempt = 1; attempt <= 3; attempt++) {
               await new Promise((resolve) => setTimeout(resolve, 2000));
               // If initAuthListener already handled sync setup, bail out gracefully
               if (isSyncRunning()) {
                  console.log('[Login] Auth listener already provisioned workspace, skipping');
                  return;
               }
               console.log(`[Login] DB credentials not ready, retry ${attempt}/3...`);
               creds = await fetchDbCredentials('/login', { firebaseToken: token, email });
               if (creds && creds.dbUrl && creds.auth?.username && creds.auth?.password) break;
            }
         }

         // Final check: auth listener may have finished provisioning during the retry loop
         if (!creds || !creds.dbUrl || !creds.auth?.username || !creds.auth?.password) {
            if (isSyncRunning()) {
               console.log('[Login] Auth listener provisioned workspace during retry, skipping');
               return;
            }
            throw new Error('Database credentials not available. Please check your connection and retry.');
         }

         // Step 3: Start replication only after successful preparation
         const couch = {
            url: creds.dbUrl,
            username: creds.auth.username,
            password: creds.auth.password,
         };
         console.log('[Login] Starting replication with credentials');
         await persistCouchCreds(userCredential.user.uid, couch);
         await startRemoteSync(userCredential.user.uid, couch);

         // Step 4: Workspace is now READY
         dispatchCouchStatus('ready', null);
         AppDispatcher.dispatch({ type: ActionTypes.WORKSPACE_READY });
      } catch (error) {
         AppDispatcher.dispatch({
            type: ActionTypes.LOGIN_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   signup: async (email: string, password: string) => {
      // Signups are handled by backend; this action is intentionally disabled.
      AppDispatcher.dispatch({
         type: ActionTypes.SIGNUP_ERROR,
         error: 'Signup is not available from the client.',
      });
   },

   logout: async () => {
      try {
         const user = auth.currentUser;

         // Clear current auth initialization
         currentAuthInitialization = null;

         // Stop replication and clean up sync resources
         stopSync();

         // Clear cached credentials
         if (user) {
            clearCouchCreds(user.uid);
         }

         // Clear attachment cache
         clearAttachmentUrlCache();

         // Sign out from Firebase
         await signOut(auth);

         // Dispatch logout action
         AppDispatcher.dispatch({
            type: ActionTypes.LOGOUT,
         });
      } catch (error) {
         console.error('Logout error:', error);
      }
   },

   deleteAccount: async (password: string): Promise<void> => {
      const user = auth.currentUser;
      if (!user?.email) {
         throw new Error('No signed-in account was found.');
      }

      try {
         const credential = EmailAuthProvider.credential(user.email, password);
         await reauthenticateWithCredential(user, credential);
         const token = await user.getIdToken(true);
         const response = await fetch(`${API_BASE}/account`, {
            method: 'DELETE',
            headers: {
               Authorization: `Bearer ${token}`,
            },
         });
         const data = (await response.json().catch(() => ({}))) as {
            message?: string;
         };

         if (!response.ok) {
            throw new Error(data.message || 'Account deletion failed. Please try again.');
         }

         currentAuthInitialization = null;
         await destroyLocalDatabase(user.uid);
         clearCouchCreds(user.uid);
         clearAttachmentUrlCache();

         try {
            await signOut(auth);
         } catch (error) {
            console.warn('Firebase sign-out after account deletion failed:', error);
         }

         AppDispatcher.dispatch({ type: ActionTypes.LOGOUT });
      } catch (error) {
         const message = getErrorMessage(error);
         throw new Error(message);
      }
   },

   refreshCouchCredentials: async () => {
      console.log('[Actions] Refreshing CouchDB credentials');
      const user = auth.currentUser;
      const result = await ensureCouchForUser({ forceReset: true });
      if (user && result.ok) {
         try {
            await startRemoteSync(user.uid, result.couch);
         } catch (err) {
            console.error('[Actions] startRemoteSync failed during credential refresh:', err);
            dispatchCouchStatus('error', getErrorMessage(err));
         }
      }
   },

   initAuthListener: () => {
      onAuthStateChanged(auth, async (user) => {
         console.log('[Auth Listener] Auth state changed, user:', user ? user.uid : 'null');
         AppDispatcher.dispatch({
            type: ActionTypes.AUTH_STATE_CHANGED,
            user,
         });

         if (!user) {
            // User logged out, clear initialization guard and stop sync
            currentAuthInitialization = null;
            stopSync();
            return;
         }

         // Prevent concurrent initializations (race condition guard)
         if (currentAuthInitialization) {
            console.log('[Auth Listener] Initialization already in progress, waiting...');
            await currentAuthInitialization;
            return;
         }

         // Create a new initialization promise
         currentAuthInitialization = (async () => {
            try {
               // Step 1: User is AUTHENTICATED - dispatch state
               AppDispatcher.dispatch({ type: ActionTypes.WORKSPACE_AUTHENTICATED });

               // Step 2: Try to migrate legacy credentials
               await migrateLegacyCredentials(user.uid);

               // Step 3: Read stored credentials
               const saved = await readCouchCreds(user.uid);
               console.log('[Auth Listener] Saved credentials:', saved ? 'Found' : 'Not found');

               // FIX 2: If login flow already started sync, don't race with it
               if (isSyncRunning()) {
                  console.log('[Auth Listener] Sync already running (login flow handled it), skipping');
                  return;
               }

               if (saved && saved.url && saved.username && saved.password) {
                  // Check if credentials are fresh
                  if (!areCredentialsFresh(saved)) {
                     if (!navigator.onLine) {
                        // Offline with stale creds — proceed with cached creds, sync retries when online
                        console.log('[Auth Listener] Offline with stale credentials - using cached creds');
                        await startRemoteSync(user.uid, saved as { url: string; username: string; password: string });
                        dispatchCouchStatus('ready', null);
                        AppDispatcher.dispatch({ type: ActionTypes.WORKSPACE_READY });
                        return;
                     }
                     console.log('[Auth Listener] Credentials are stale, refreshing from backend');
                     const result = await ensureCouchForUser({ forceReset: false });
                     if (result.ok) {
                        await startRemoteSync(user.uid, result.couch);
                     }
                     return;
                  }

                  // Try to start sync with saved credentials
                  try {
                     console.log('[Auth Listener] Starting remote sync with saved credentials');
                     await startRemoteSync(user.uid, saved as { url: string; username: string; password: string });
                     dispatchCouchStatus('ready', null);
                     AppDispatcher.dispatch({ type: ActionTypes.WORKSPACE_READY });
                  } catch (err) {
                     console.warn('Could not start sync on auth change', err);
                     const message = String((err as { message?: string })?.message || '');
                     if (message.includes('CouchDB authentication failed')) {
                        const result = await ensureCouchForUser({ forceReset: true });
                        if (result.ok) {
                           await startRemoteSync(user.uid, result.couch);
                        }
                     } else {
                        throw err;
                     }
                  }
               } else {
                  // No saved credentials (e.g. first load on a new domain): bootstrap from backend.
                  console.log('[Auth Listener] No saved credentials found - initializing from backend');
                  dispatchCouchStatus('pending', 'Database setup in progress...');
                  const result = await ensureCouchForUser({ forceReset: false });
                  if (result.ok) {
                     await startRemoteSync(user.uid, result.couch);
                  }
               }
            } catch (err) {
               console.error('Auth initialization error:', err);
               dispatchCouchStatus('error', 'Failed to initialize database connection');
            }
         })();

         await currentAuthInitialization;
         currentAuthInitialization = null;
      });
   },

   // Project Actions
   loadProjects: async (userId: string) => {
      try {
         const saved = await readCouchCreds(userId);
         // Ensure Pouch is ready for this user (no-op if already initialized)
         if (saved && saved.url) {
            await initPouch(userId, saved);
         } else {
            throw new Error('Database credentials are unavailable. Please log in again while online.');
         }
         const projects = await fetchByType<Project>(DocumentType.PROJECT, userId);
         AppDispatcher.dispatch({
            type: ActionTypes.PROJECTS_LOADED,
            projects,
         });
      } catch (error) {
         console.error('Load projects error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   buildProjectFile: async (projectId: string): Promise<QuillProjectFile> => {
      const userId = requireUserId();
      const [projects, scenes, characters, locations, plots] = await Promise.all([
         fetchByType<Project>(DocumentType.PROJECT, userId),
         fetchByType<Scene>(DocumentType.SCENE, userId),
         fetchByType<Character>(DocumentType.CHARACTER, userId),
         fetchByType<Location>(DocumentType.LOCATION, userId),
         fetchByType<Plot>(DocumentType.PLOT, userId),
      ]);
      const project = projects.find((candidate) => candidate.id === projectId);
      if (!project) {
         throw new Error('The selected project could not be found.');
      }

      const projectScenes = scenes.filter((scene) => scene.projectId === projectId);
      const projectCharacters = characters.filter(
         (character) => character.projectId === projectId,
      );
      const projectLocations = locations.filter(
         (location) => location.projectId === projectId,
      );
      const projectPlots = plots.filter((plot) => plot.projectId === projectId);
      const assets: QuillProjectAsset[] = [];

      for (const [ownerType, entities] of [
         ['character', projectCharacters],
         ['location', projectLocations],
      ] as const) {
         for (const entity of entities) {
            if (!entity.id || !entity.imageAttachmentId) continue;
            const blob = await getAttachmentBlob(entity.id, entity.imageAttachmentId);
            if (!blob) {
               throw new Error(
                  `The image attached to "${entity.name || entity.id}" could not be read.`,
               );
            }
            assets.push({
               ownerType,
               ownerId: entity.id,
               attachmentId: entity.imageAttachmentId,
               contentType: blob.type || 'application/octet-stream',
               data: await blobToBase64(blob),
            });
         }
      }

      return {
         format: QUILL_PROJECT_FORMAT,
         version: QUILL_PROJECT_VERSION,
         exportedAt: new Date().toISOString(),
         project: stripProjectFileInternals(project as any),
         scenes: projectScenes.map((scene) => stripProjectFileInternals(scene as any)),
         characters: projectCharacters.map((character) =>
            stripProjectFileInternals(character as any),
         ),
         locations: projectLocations.map((location) =>
            stripProjectFileInternals(location as any),
         ),
         plots: projectPlots.map((plot) => stripProjectFileInternals(plot as any)),
         assets,
      };
   },

   importProjectFile: async (data: QuillProjectFile): Promise<string> => {
      const userId = requireUserId();
      const createdDocIds: string[] = [];
      const characterIdMap = new Map<string, string>();
      const locationIdMap = new Map<string, string>();
      const sceneIdMap = new Map<string, string>();
      const plotIdMap = new Map<string, string>();
      const assetsByOwner = new Map<string, QuillProjectAsset[]>();

      for (const asset of data.assets) {
         const key = `${asset.ownerType}:${asset.ownerId}`;
         const ownerAssets = assetsByOwner.get(key) || [];
         ownerAssets.push(asset);
         assetsByOwner.set(key, ownerAssets);
      }

      try {
         let project = await createDoc<Project>(
            DocumentType.PROJECT,
            cleanImportedEntity(data.project),
            userId,
         );
         createdDocIds.push(project.id!);
         const importedCharacters: Character[] = [];
         const importedLocations: Location[] = [];
         const importedScenes: Scene[] = [];
         const importedPlots: Plot[] = [];

         for (const source of data.characters) {
            const oldId = source.id!;
            let character = await createDoc<Character>(
               DocumentType.CHARACTER,
               {
                  ...cleanImportedEntity(source),
                  projectId: project.id,
               },
               userId,
            );
            createdDocIds.push(character.id!);
            characterIdMap.set(oldId, character.id!);

            for (const asset of assetsByOwner.get(`character:${oldId}`) || []) {
               await putDocAttachment(
                  character.id!,
                  asset.attachmentId,
                  base64ToBlob(asset.data, asset.contentType),
                  asset.contentType,
               );
               const imageUrl = await getAttachmentUrl(
                  character.id!,
                  asset.attachmentId,
               );
               character = { ...character, imageUrl };
            }
            importedCharacters.push(character);
         }

         for (const source of data.locations) {
            const oldId = source.id!;
            let location = await createDoc<Location>(
               DocumentType.LOCATION,
               {
                  ...cleanImportedEntity(source),
                  projectId: project.id,
               },
               userId,
            );
            createdDocIds.push(location.id!);
            locationIdMap.set(oldId, location.id!);

            for (const asset of assetsByOwner.get(`location:${oldId}`) || []) {
               await putDocAttachment(
                  location.id!,
                  asset.attachmentId,
                  base64ToBlob(asset.data, asset.contentType),
                  asset.contentType,
               );
               const imageUrl = await getAttachmentUrl(
                  location.id!,
                  asset.attachmentId,
               );
               location = { ...location, imageUrl };
            }
            importedLocations.push(location);
         }

         for (const source of data.scenes) {
            const oldId = source.id!;
            const sourceData = cleanImportedEntity(source);
            const sceneData: Partial<Scene> = {
               ...sourceData,
               projectId: project.id,
            };
            const characterIds = remapIds(source.characterIds, characterIdMap);
            if (characterIds) {
               sceneData.characterIds = characterIds;
            }
            if (source.povCharacterId !== undefined) {
               sceneData.povCharacterId = source.povCharacterId
                  ? characterIdMap.get(source.povCharacterId) || null
                  : null;
            }
            const scene = await createDoc<Scene>(
               DocumentType.SCENE,
               sceneData,
               userId,
            );
            createdDocIds.push(scene.id!);
            sceneIdMap.set(oldId, scene.id!);
            importedScenes.push(scene);
         }

         for (const source of data.plots) {
            const oldId = source.id!;
            const plot = await createDoc<Plot>(
               DocumentType.PLOT,
               {
                  ...cleanImportedEntity(source),
                  projectId: project.id,
               },
               userId,
            );
            createdDocIds.push(plot.id!);
            plotIdMap.set(oldId, plot.id!);
            importedPlots.push(plot);
         }

         const sourceProject = data.project as Record<string, unknown>;
         const relationshipUpdates: Record<string, string[]> = {};
         for (const [field, idMap] of [
            ['sceneIds', sceneIdMap],
            ['characterIds', characterIdMap],
            ['locationIds', locationIdMap],
            ['plotIds', plotIdMap],
         ] as const) {
            const remapped = remapIds(sourceProject[field], idMap);
            if (remapped) relationshipUpdates[field] = remapped;
         }
         if (Object.keys(relationshipUpdates).length > 0) {
            project = await updateDocById<Project>(
               project.id!,
               relationshipUpdates as Partial<Project>,
            );
         }

         AppDispatcher.dispatch({
            type: ActionTypes.CREATE_PROJECT,
            project,
         });
         AppDispatcher.dispatch({
            type: ActionTypes.SCENES_LOADED,
            scenes: importedScenes,
         });
         AppDispatcher.dispatch({
            type: ActionTypes.CHARACTERS_LOADED,
            characters: importedCharacters,
         });
         AppDispatcher.dispatch({
            type: ActionTypes.LOCATIONS_LOADED,
            locations: importedLocations,
         });
         AppDispatcher.dispatch({
            type: ActionTypes.PLOTS_LOADED,
            plots: importedPlots,
         });

         return project.id!;
      } catch (error) {
         for (const docId of createdDocIds.reverse()) {
            try {
               await deleteDocById(docId);
            } catch (rollbackError) {
               console.error('Unable to roll back imported document:', {
                  docId,
                  rollbackError,
               });
            }
         }
         throw new Error(`Project import failed: ${getErrorMessage(error)}`);
      }
   },

   createProject: async (projectData: Project, userId: string) => {
      try {
         const project = await createDoc<Project>(DocumentType.PROJECT, projectData, userId);
         AppDispatcher.dispatch({
            type: ActionTypes.CREATE_PROJECT,
            project,
         });
         return project.id;
      } catch (error) {
         console.error('Create project error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   updateProject: async (projectId: string, updates: Partial<Project>) => {
      try {
         const updated = await updateDocById<Project>(projectId, updates);
         AppDispatcher.dispatch({
            type: ActionTypes.UPDATE_PROJECT,
            projectId,
            updates: updated,
         });
      } catch (error) {
         console.error('Update project error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   deleteProject: async (projectId: string) => {
      try {
         await deleteDocById(projectId);
         AppDispatcher.dispatch({
            type: ActionTypes.DELETE_PROJECT,
            projectId,
         });
      } catch (error) {
         console.error('Delete project error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   selectProject: (projectId: string | null) => {
      AppDispatcher.dispatch({
         type: ActionTypes.SELECT_PROJECT,
         projectId,
      });
   },

   // Scene Actions
   loadScenes: async (projectId: string) => {
      try {
         const scenes = (await fetchByType<Scene>(DocumentType.SCENE, requireUserId()))
            .filter((s) => s.projectId === projectId)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
         AppDispatcher.dispatch({
            type: ActionTypes.SCENES_LOADED,
            scenes,
         });
      } catch (error) {
         console.error('Load scenes error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   createScene: async (sceneData: Scene, projectId: string) => {
      try {
         const scene = await createDoc<Scene>(
            DocumentType.SCENE,
            { ...sceneData, projectId },
            requireUserId(),
         );
         AppDispatcher.dispatch({
            type: ActionTypes.CREATE_SCENE,
            scene,
         });
         return scene.id;
      } catch (error) {
         console.error('Create scene error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   updateScene: async (sceneId: string, updates: Partial<Scene>) => {
      try {
         await updateDocById<Scene>(sceneId, updates);
         AppDispatcher.dispatch({
            type: ActionTypes.UPDATE_SCENE,
            sceneId,
            updates,
         });
      } catch (error) {
         console.error('Update scene error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   deleteScene: async (sceneId: string) => {
      try {
         await deleteDocById(sceneId);
         AppDispatcher.dispatch({
            type: ActionTypes.DELETE_SCENE,
            sceneId,
         });
      } catch (error) {
         console.error('Delete scene error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   selectScene: (sceneId: string) => {
      AppDispatcher.dispatch({
         type: ActionTypes.SELECT_SCENE,
         sceneId,
      });
   },

   // Character Actions
   loadCharacters: async (projectId: string) => {
      try {
         const characters = (await fetchByType<Character>(DocumentType.CHARACTER, requireUserId()))
            .filter((c) => c.projectId === projectId)
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
         const hydrated = await Promise.all(characters.map(hydrateImageUrl));
         AppDispatcher.dispatch({
            type: ActionTypes.CHARACTERS_LOADED,
            characters: hydrated,
         });
      } catch (error) {
         console.error('Load characters error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   createCharacter: async (
      characterData: Character & { imageFile?: File | null },
      projectId: string,
   ) => {
      try {
         const { imageFile, ...rest } = characterData;
         const base = await createDoc<Character>(
            DocumentType.CHARACTER,
            { ...rest, projectId },
            requireUserId(),
         );
         let character = base;
         if (imageFile) {
            character = await attachImageToDoc<Character>(base.id!, imageFile);
         }
         AppDispatcher.dispatch({
            type: ActionTypes.CREATE_CHARACTER,
            character,
         });
         return character.id;
      } catch (error) {
         console.error('Create character error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   updateCharacter: async (
      characterId: string,
      updates: Partial<Character> & { imageFile?: File | null },
   ) => {
      try {
         const { imageFile, ...rest } = updates;
         const sanitized = { ...rest } as Partial<Character>;
         if (!shouldPersistImageUrl(sanitized.imageUrl)) {
            delete sanitized.imageUrl;
         }
         let updated = await updateDocById<Character>(characterId, sanitized);
         if (imageFile) {
            updated = await attachImageToDoc<Character>(characterId, imageFile);
         } else {
            updated = await hydrateImageUrl(updated);
         }
         AppDispatcher.dispatch({
            type: ActionTypes.UPDATE_CHARACTER,
            characterId,
            updates: updated,
         });
      } catch (error) {
         console.error('Update character error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   deleteCharacter: async (characterId: string) => {
      try {
         revokeAttachmentUrl(characterId, imageAttachmentId);
         await deleteDocById(characterId);
         AppDispatcher.dispatch({
            type: ActionTypes.DELETE_CHARACTER,
            characterId,
         });
      } catch (error) {
         console.error('Delete character error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   selectCharacter: (characterId: string) => {
      AppDispatcher.dispatch({
         type: ActionTypes.SELECT_CHARACTER,
         characterId,
      });
   },

   // Location Actions
   loadLocations: async (projectId: string) => {
      try {
         const locations = (await fetchByType<Location>(DocumentType.LOCATION, requireUserId()))
            .filter((l) => l.projectId === projectId)
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
         const hydrated = await Promise.all(locations.map(hydrateImageUrl));
         AppDispatcher.dispatch({
            type: ActionTypes.LOCATIONS_LOADED,
            locations: hydrated,
         });
      } catch (error) {
         console.error('Load locations error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   createLocation: async (
      locationData: Location & { imageFile?: File | null },
      projectId: string,
   ) => {
      try {
         const { imageFile, ...rest } = locationData;
         const base = await createDoc<Location>(
            DocumentType.LOCATION,
            { ...rest, projectId },
            requireUserId(),
         );
         let location = base;
         if (imageFile) {
            location = await attachImageToDoc<Location>(base.id!, imageFile);
         }
         AppDispatcher.dispatch({
            type: ActionTypes.CREATE_LOCATION,
            location,
         });
         return location.id;
      } catch (error) {
         console.error('Create location error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   updateLocation: async (
      locationId: string,
      updates: Partial<Location> & { imageFile?: File | null },
   ) => {
      try {
         const { imageFile, ...rest } = updates;
         const sanitized = { ...rest } as Partial<Location>;
         if (!shouldPersistImageUrl(sanitized.imageUrl)) {
            delete sanitized.imageUrl;
         }
         let updated = await updateDocById<Location>(locationId, sanitized);
         if (imageFile) {
            updated = await attachImageToDoc<Location>(locationId, imageFile);
         } else {
            updated = await hydrateImageUrl(updated);
         }
         AppDispatcher.dispatch({
            type: ActionTypes.UPDATE_LOCATION,
            locationId,
            updates: updated,
         });
      } catch (error) {
         console.error('Update location error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   deleteLocation: async (locationId: string) => {
      try {
         revokeAttachmentUrl(locationId, imageAttachmentId);
         await deleteDocById(locationId);
         AppDispatcher.dispatch({
            type: ActionTypes.DELETE_LOCATION,
            locationId,
         });
      } catch (error) {
         console.error('Delete location error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   selectLocation: (locationId: string) => {
      AppDispatcher.dispatch({
         type: ActionTypes.SELECT_LOCATION,
         locationId,
      });
   },

   // Plot Actions
   loadPlots: async (projectId: string) => {
      try {
         const plots = (await fetchByType<Plot>(DocumentType.PLOT, requireUserId()))
            .filter((p) => p.projectId === projectId)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
         AppDispatcher.dispatch({
            type: ActionTypes.PLOTS_LOADED,
            plots,
         });
      } catch (error) {
         console.error('Load plots error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   createPlot: async (plotData: Plot, projectId: string) => {
      try {
         const plot = await createDoc<Plot>(
            DocumentType.PLOT,
            { ...plotData, projectId },
            requireUserId(),
         );
         AppDispatcher.dispatch({
            type: ActionTypes.CREATE_PLOT,
            plot,
         });
         return plot.id;
      } catch (error) {
         console.error('Create plot error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   updatePlot: async (plotId: string, updates: Partial<Plot>) => {
      try {
         const updated = await updateDocById<Plot>(plotId, updates);
         AppDispatcher.dispatch({
            type: ActionTypes.UPDATE_PLOT,
            plotId,
            updates: updated,
         });
      } catch (error) {
         console.error('Update plot error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   deletePlot: async (plotId: string) => {
      try {
         await deleteDocById(plotId);
         AppDispatcher.dispatch({
            type: ActionTypes.DELETE_PLOT,
            plotId,
         });
      } catch (error) {
         console.error('Delete plot error:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },

   selectPlot: (plotId: string) => {
      AppDispatcher.dispatch({
         type: ActionTypes.SELECT_PLOT,
         plotId,
      });
   },

   // UI Actions
   setActiveTab: (tab: string) => {
      AppDispatcher.dispatch({
         type: ActionTypes.SET_ACTIVE_TAB,
         tab,
      });
   },

   toggleSidebar: () => {
      AppDispatcher.dispatch({
         type: ActionTypes.TOGGLE_SIDEBAR,
      });
   },

   showModal: (modalType: string, modalData: Record<string, unknown> = {}) => {
      AppDispatcher.dispatch({
         type: ActionTypes.SHOW_MODAL,
         modalType,
         modalData,
      });
   },

   hideModal: () => {
      AppDispatcher.dispatch({
         type: ActionTypes.HIDE_MODAL,
      });
   },

   setLoading: (isLoading: boolean) => {
      AppDispatcher.dispatch({
         type: ActionTypes.SET_LOADING,
         isLoading,
      });
   },

   setError: (error: string) => {
      AppDispatcher.dispatch({
         type: ActionTypes.SET_ERROR,
         error,
      });
   },

   clearError: () => {
      AppDispatcher.dispatch({
         type: ActionTypes.CLEAR_ERROR,
      });
   },

   retrySyncAfterBlock: async () => {
      try {
         await resetSyncBlock();
         AppDispatcher.dispatch({
            type: ActionTypes.COUCH_STATUS_CHANGED,
            couchStatus: 'pending',
            couchMessage: 'Retrying sync...',
         });
      } catch (error) {
         console.error('Failed to reset sync block:', error);
         AppDispatcher.dispatch({
            type: ActionTypes.SET_ERROR,
            error: getErrorMessage(error),
         });
      }
   },
};

export default Actions;

type DbCredentialsResponse = {
   dbUrl?: string;
   auth?: { username?: string; password?: string };
   status?: CouchStatus | 'offline';
   message?: string;
};

const fetchDbCredentials = async (
   path: string,
   payload: Record<string, unknown>,
): Promise<DbCredentialsResponse | null> => {
   try {
      const res = await fetch(`${API_BASE}${path}`, {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         body: JSON.stringify(payload),
      });
      if (!res.ok) {
         const message = await res.text();
         throw new Error(message || `API error: ${res.status}`);
      }
      const data = await res.json();
      return { dbUrl: data.dbUrl, auth: data.auth, status: data.status, message: data.message };
   } catch (err) {
      console.error('DB credentials fetch failed:', err);
      return null;
   }
};
